import React, { useState, useEffect } from 'react';
import {
  Settings,
  User,
  Bell,
  Shield,
  Globe,
  Palette,
  Database,
  LogOut,
  Moon,
  Sun,
  Volume2,
  VolumeX,
  Mail,
  Phone,
  Lock,
  Save,
  RefreshCw,
  X,
  Check,
  AlertCircle
} from 'lucide-react';

const SettingsPage = ({ user, onClose, onLogout, onUpdateUser }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [settings, setSettings] = useState({
    // Paramètres profil
    username: user?.username || '',
    email: user?.email || '',
    phone: user?.phone_number || '',
    language: user?.language || 'fr',
    country: user?.country || 'KE',
    
    // Paramètres notifications
    emailNotifications: true,
    smsNotifications: true,
    pushNotifications: true,
    groupUpdates: true,
    paymentReminders: true,
    
    // Paramètres affichage
    theme: 'light',
    fontSize: 'medium',
    currency: 'KES',
    
    // Paramètres sécurité
    twoFactorEnabled: false,
    sessionTimeout: 30,
    
    // Paramètres système
    autoSync: true,
    offlineMode: false,
    debugMode: false,
    
    // Paramètres maintenance (super-admin uniquement)
    maintenanceMode: false,
    maintenanceMessage: 'Application en cours de mise à jour. Merci de revenir plus tard.',
    maintenanceEstimatedTime: '30-60 minutes'
  });

  const [feedback, setFeedback] = useState({ type: '', message: '', visible: false });
  const [loading, setLoading] = useState(false);

  const showFeedback = (type, message) => {
    setFeedback({ type, message, visible: true });
    setTimeout(() => {
      setFeedback({ type: '', message: '', visible: false });
    }, 3000);
  };

  const handleSave = async () => {
    setLoading(true);
    
    try {
      // Ici, vous pouvez ajouter l'appel API pour sauvegarder les paramètres
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulation
      
      showFeedback('success', 'Paramètres sauvegardés avec succès !');
      
      // Mise à jour des données utilisateur si nécessaire
      if (onUpdateUser) {
        onUpdateUser({
          ...user,
          username: settings.username,
          language: settings.language,
          country: settings.country
        });
      }
      
    } catch (error) {
      showFeedback('error', 'Erreur lors de la sauvegarde des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profil', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'display', label: 'Affichage', icon: Palette },
    { id: 'security', label: 'Sécurité', icon: Shield },
    { id: 'system', label: 'Système', icon: Database }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Settings className="w-8 h-8" />
              <div>
                <h1 className="text-2xl font-bold">Paramètres</h1>
                <p className="text-blue-100">Configurez votre application Likico</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Feedback */}
        {feedback.visible && (
          <div className={`mx-6 mt-4 p-4 rounded-lg flex items-center space-x-3 ${
            feedback.type === 'success' ? 'bg-green-100 text-green-800 border-l-4 border-green-500' :
            feedback.type === 'error' ? 'bg-red-100 text-red-800 border-l-4 border-red-500' :
            'bg-blue-100 text-blue-800 border-l-4 border-blue-500'
          }`}>
            {feedback.type === 'success' && <Check className="w-5 h-5" />}
            {feedback.type === 'error' && <AlertCircle className="w-5 h-5" />}
            <span>{feedback.message}</span>
          </div>
        )}

        <div className="flex">
          {/* Sidebar */}
          <div className="w-64 bg-gray-50 p-4">
            <div className="space-y-2">
              {tabs.map(tab => {
                const IconComponent = tab.icon;
                const isActive = activeTab === tab.id;
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors text-left ${
                      isActive 
                        ? 'bg-blue-100 text-blue-600 font-medium' 
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <IconComponent className="w-5 h-5" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
              
              {/* Bouton Déconnexion */}
              <div className="pt-4 mt-4 border-t border-gray-200">
                <button
                  onClick={onLogout}
                  className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors text-left"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Déconnexion</span>
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            {/* Profil */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900">Informations du profil</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom d'utilisateur
                    </label>
                    <input
                      type="text"
                      value={settings.username}
                      onChange={(e) => setSettings({...settings, username: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={settings.email}
                      onChange={(e) => setSettings({...settings, email: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      value={settings.phone}
                      onChange={(e) => setSettings({...settings, phone: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Langue
                    </label>
                    <select
                      value={settings.language}
                      onChange={(e) => setSettings({...settings, language: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="fr">Français</option>
                      <option value="en">English</option>
                      <option value="sw">Kiswahili</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Notifications */}
            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900">Préférences de notification</h2>
                
                <div className="space-y-4">
                  {[
                    { key: 'emailNotifications', label: 'Notifications par email', desc: 'Recevoir les notifications importantes par email' },
                    { key: 'smsNotifications', label: 'Notifications SMS', desc: 'Recevoir les alertes de paiement par SMS' },
                    { key: 'pushNotifications', label: 'Notifications push', desc: 'Notifications en temps réel sur l\'appareil' },
                    { key: 'groupUpdates', label: 'Mises à jour des groupes', desc: 'Être notifié des activités dans vos groupes' },
                    { key: 'paymentReminders', label: 'Rappels de paiement', desc: 'Rappels automatiques avant les échéances' }
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <h3 className="font-medium text-gray-900">{item.label}</h3>
                        <p className="text-sm text-gray-600">{item.desc}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings[item.key]}
                          onChange={(e) => setSettings({...settings, [item.key]: e.target.checked})}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Affichage */}
            {activeTab === 'display' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900">Préférences d'affichage</h2>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Thème
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        onClick={() => setSettings({...settings, theme: 'light'})}
                        className={`p-4 rounded-lg border-2 flex items-center space-x-3 ${
                          settings.theme === 'light' 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Sun className="w-6 h-6" />
                        <span>Clair</span>
                      </button>
                      <button
                        onClick={() => setSettings({...settings, theme: 'dark'})}
                        className={`p-4 rounded-lg border-2 flex items-center space-x-3 ${
                          settings.theme === 'dark' 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Moon className="w-6 h-6" />
                        <span>Sombre</span>
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Taille de police
                    </label>
                    <select
                      value={settings.fontSize}
                      onChange={(e) => setSettings({...settings, fontSize: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="small">Petite</option>
                      <option value="medium">Moyenne</option>
                      <option value="large">Grande</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Devise par défaut
                    </label>
                    <select
                      value={settings.currency}
                      onChange={(e) => setSettings({...settings, currency: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="KES">KES - Kenyan Shilling</option>
                      <option value="USD">USD - US Dollar</option>
                      <option value="EUR">EUR - Euro</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Sécurité */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900">Sécurité et confidentialité</h2>
                
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-gray-900">Authentification à deux facteurs</h3>
                        <p className="text-sm text-gray-600">Sécurité renforcée pour votre compte</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.twoFactorEnabled}
                          onChange={(e) => setSettings({...settings, twoFactorEnabled: e.target.checked})}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Timeout de session (minutes)
                    </label>
                    <select
                      value={settings.sessionTimeout}
                      onChange={(e) => setSettings({...settings, sessionTimeout: parseInt(e.target.value)})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={15}>15 minutes</option>
                      <option value={30}>30 minutes</option>
                      <option value={60}>1 heure</option>
                      <option value={120}>2 heures</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Système */}
            {activeTab === 'system' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900">Paramètres système</h2>
                
                <div className="space-y-4">
                  {[
                    { key: 'autoSync', label: 'Synchronisation automatique', desc: 'Synchroniser les données automatiquement' },
                    { key: 'offlineMode', label: 'Mode hors ligne', desc: 'Permettre l\'utilisation sans connexion internet' },
                    { key: 'debugMode', label: 'Mode débogage', desc: 'Activer les logs détaillés (pour les développeurs)' }
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <h3 className="font-medium text-gray-900">{item.label}</h3>
                        <p className="text-sm text-gray-600">{item.desc}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings[item.key]}
                          onChange={(e) => setSettings({...settings, [item.key]: e.target.checked})}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  ))}
                  
                  <div className="pt-4 border-t">
                    <h3 className="font-medium text-gray-900 mb-3">Informations de l'application</h3>
                    <div className="text-sm text-gray-600 space-y-2">
                      <p>Version : 1.0.0</p>
                      <p>Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}</p>
                      <p>Utilisateur : {user?.username} ({user?.role})</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-between items-center">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Annuler
          </button>
          
          <div className="flex space-x-3">
            <button
              onClick={() => window.location.reload()}
              className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Recharger</span>
            </button>
            
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Sauvegarde...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Sauvegarder</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;